import styled from '@emotion/styled';
import { OneMinuteInMillis, OneSecondInMillis } from '@racemap/utilities/consts/time';
import { getRelativeTime } from '@racemap/utilities/functions/datetime';
import { Button, Dropdown, Flex, Space, Typography } from 'antd';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { z } from 'zod';
import { useCurrentDateTime } from '../../lib/customHooks/useCurrentDateTime';
import { IconChevronDown, IconRefresh } from './Icons';

const { Text } = Typography;

const RefreshIntervalSchema = z.enum(['off', '30s', '1m', '5m', '15m']);

export type RefreshInterval = z.infer<typeof RefreshIntervalSchema>;

/** The intervals the picker offers, as the URL keeps them (`?refresh=1m`), mapped to milliseconds. */
export const RefreshIntervalsMs: Record<RefreshInterval, number> = {
  off: 0,
  '30s': 30 * OneSecondInMillis,
  '1m': OneMinuteInMillis,
  '5m': 5 * OneMinuteInMillis,
  '15m': 15 * OneMinuteInMillis,
};

/** An old bookmark can carry any `?refresh=` value; anything unknown reads as off. */
export const toRefreshInterval = (value: string | null | undefined): RefreshInterval =>
  RefreshIntervalSchema.catch('off').parse(value);

const IntervalLabels: Record<RefreshInterval, string> = {
  off: 'Off',
  '30s': '30 s',
  '1m': '1 m',
  '5m': '5 m',
  '15m': '15 m',
};

const IntervalItems = Object.entries(IntervalLabels).map(([key, label]) => ({ key, label }));

interface Props {
  value: RefreshInterval;
  onChange: (value: RefreshInterval) => void;
  onRefresh: () => void;
  /** When the shown data was last fetched. `null` before the first load. */
  lastUpdatedAt?: Date | null;
  isRefreshing?: boolean;
}

/**
 * Reload button plus an auto refresh interval, in the shape Grafana uses.
 *
 * The active interval sits on the trigger rather than only inside the dropdown: a shared link
 * carries `?refresh=` and starts polling in the recipient's browser, which has to be visible.
 */
export const RefreshPicker: FC<Props> = ({
  value,
  onChange,
  onRefresh,
  lastUpdatedAt,
  isRefreshing = false,
}) => (
  <Flex align="center" gap={8}>
    <Space.Compact>
      <ReloadButton
        // the spinner's own aria-label would otherwise become the button's name while loading
        aria-label="Reload"
        title="Reload"
        icon={<IconRefresh />}
        loading={isRefreshing}
        onClick={onRefresh}
      >
        <ReloadLabel>Reload</ReloadLabel>
      </ReloadButton>
      <Dropdown
        trigger={['click']}
        menu={{
          items: IntervalItems,
          selectable: true,
          selectedKeys: [value],
          onSelect: ({ key }) => onChange(key as RefreshInterval),
        }}
      >
        <Button title="Auto refresh interval" icon={<IconChevronDown />} iconPlacement="end">
          {value === 'off' ? null : IntervalLabels[value]}
        </Button>
      </Dropdown>
    </Space.Compact>
    {lastUpdatedAt != null && <DataAge since={lastUpdatedAt} />}
  </Flex>
);

/** Below this the toolbar is tight enough that the reload button drops to its icon. */
const LabelBreakpoint = 1200;

const ReloadLabel = styled.span`
  @media (max-width: ${LabelBreakpoint}px) {
    display: none;
  }
`;

/** Without this the button keeps its text-sized padding around a hidden label. */
const ReloadButton = styled(Button)`
  @media (max-width: ${LabelBreakpoint}px) {
    padding-inline: 8px;
  }
`;

/**
 * Counts the seconds rather than reusing `RelativeTime`: below a minute that renders
 * "few sec", which cannot tell a fresh view from one that is a whole poll interval stale —
 * the one thing this label exists to answer.
 */
const DataAge: FC<{ since: Date }> = ({ since }) => {
  const now = useCurrentDateTime();
  const seconds = Math.max(0, Math.round(now.diff(DateTime.fromJSDate(since)).as('seconds')));

  return (
    <Text type="secondary">
      updated {seconds < 60 ? `${seconds} s` : getRelativeTime(since, now, 'short')} ago
    </Text>
  );
};
